import { useEffect, useMemo } from "react";

import { toErrorMessage } from "../../../../components/Input/InstrumentSearch";
import {
  useGetAdminCorporateActionsQuery,
  useGetAdminInstrumentTodosQuery,
} from "../../../../services/admin";
import { useSetInstrumentSourceCountryMutation } from "../../../../services/instruments";
import {
  PendingTickerRequest,
  useApproveTickerRequestMutation,
  useGetPendingTickerRequestsQuery,
  useRejectTickerRequestMutation,
} from "../../../../services/tickerRequests";
import {
  InstrumentFixErrors,
  InstrumentSelection,
} from "../../components/InstrumentEditor";
import { WORK_CATEGORIES } from "../constants";
import { WorkItem, WorkCategoryFilter, WorkKind } from "../types";
import { countByCategory, filterWorkItems } from "../utils/filterWorkItems";
import { withoutPricingSections } from "../../components/InstrumentEditor";
import {
  isReturnedFix,
  requestKey,
  toOpenActionItem,
  toPendingFixItems,
  toPendingItem,
  toResolvedItem,
} from "../utils/toWorkItem";
import { useWorkQueueSession } from "../WorkQueueProvider";
import { useCorporateActionActions } from "./useCorporateActionActions";
import { useInstrumentFixActions } from "./useInstrumentFixActions";

const NO_REQUESTS: PendingTickerRequest[] = [];

const labelOf = (category: WorkKind) =>
  WORK_CATEGORIES.find(({ kind }) => kind === category)?.label ?? category;

export const useWorkQueue = () => {
  const requestsRead = useGetPendingTickerRequestsQuery();
  const fixesRead = useGetAdminInstrumentTodosQuery();
  const actionsRead = useGetAdminCorporateActionsQuery();
  const [approveRequest, { isLoading: isApproving }] =
    useApproveTickerRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectTickerRequestMutation();
  const [setSourceCountry, { isLoading: isSavingCountry }] =
    useSetInstrumentSourceCountryMutation();
  const { isFixing, saveCountry, repair, applyManualPricing } =
    useInstrumentFixActions();
  const { isActing, deferAction, resumeAction, reconcileAction } =
    useCorporateActionActions();
  const {
    view,
    resolved,
    outcomeKey,
    failure,
    changeFilters,
    changeOrder,
    updateDrafts,
    pruneDrafts,
    clearOutcome,
    recordDecision,
    dropResolution,
    setDecisionWarning,
    setFailure,
  } = useWorkQueueSession();

  const requestData = requestsRead.data;
  const fixData = fixesRead.data;
  const pendingFixes = useMemo(
    () => (fixData ? toPendingFixItems(fixData) : []),
    [fixData],
  );
  const actionData = actionsRead.data;
  const openActions = useMemo(
    () => (actionData ?? []).map(toOpenActionItem),
    [actionData],
  );
  const fixesSettledAt = fixesRead.isFetching
    ? null
    : (fixesRead.fulfilledTimeStamp ?? null);
  const returnedKeys = useMemo(
    () =>
      resolved
        .filter((resolution) =>
          isReturnedFix(resolution, pendingFixes, fixesSettledAt),
        )
        .map(({ key }) => key),
    [resolved, pendingFixes, fixesSettledAt],
  );

  useEffect(() => {
    if (requestData) {
      pruneDrafts(
        "request",
        requestData.map(({ id }) => requestKey(id)),
      );
    }
  }, [requestData, pruneDrafts]);

  useEffect(() => {
    if (fixData) {
      pruneDrafts(
        "instrument",
        pendingFixes.map(({ key }) => key),
      );
      updateDrafts(
        "instrument",
        pendingFixes
          .filter(({ needsPricing }) => !needsPricing)
          .map(({ key }) => key),
        withoutPricingSections,
      );
    }
  }, [fixData, pendingFixes, pruneDrafts, updateDrafts]);

  useEffect(() => {
    if (actionData) {
      pruneDrafts(
        "corporate",
        openActions.map(({ key }) => key),
      );
    }
  }, [actionData, openActions, pruneDrafts]);

  useEffect(() => {
    returnedKeys.forEach(dropResolution);
  }, [returnedKeys, dropResolution]);

  useEffect(() => clearOutcome, [clearOutcome]);

  const requests = requestData ?? NO_REQUESTS;
  const standingResolutions = resolved.filter(
    ({ key }) => !returnedKeys.includes(key),
  );
  const resolvedItems = standingResolutions.map(toResolvedItem);
  const resolvedKeys = new Set(standingResolutions.map(({ key }) => key));
  // Excludes decided rows the refetch hasn't dropped yet, so Next item can't land on one.
  const openItems = [
    ...requests.map(toPendingItem),
    ...pendingFixes,
    ...openActions,
  ].filter(({ key }) => !resolvedKeys.has(key));
  const allItems: WorkItem[] = [...openItems, ...resolvedItems];

  const items = filterWorkItems(allItems, view);
  const counts = countByCategory(allItems, view.status);
  const outcome = resolvedItems.find(({ key }) => key === outcomeKey) ?? null;
  const selected =
    outcome ??
    items.find(({ key }) => key === view.selectedKey) ??
    items[0] ??
    null;
  const nextItem = items.find(({ status }) => status === "pending") ?? null;

  const actionError = failure?.key === selected?.key ? failure?.message : null;
  const fixErrors: InstrumentFixErrors =
    failure?.section && failure.key === selected?.key
      ? { [failure.section]: failure.message }
      : {};
  const detachedError =
    failure && failure.key !== selected?.key
      ? `${failure.symbol}: ${failure.message}`
      : null;

  const failWith = (request: PendingTickerRequest, err: unknown) =>
    setFailure({
      key: requestKey(request.id),
      symbol: request.symbol,
      message: toErrorMessage(err),
    });

  const select = (key: string) => {
    setFailure(null);
    changeFilters({ selectedKey: key });
  };

  const changeCategory = (category: WorkCategoryFilter) =>
    changeFilters({ category, selectedKey: null });

  const approve = async (
    request: PendingTickerRequest,
    selection: InstrumentSelection,
  ) => {
    setFailure(null);
    const { sourceCountry, ...approval } = selection;
    const key = requestKey(request.id);
    try {
      const { instrument } = await approveRequest({
        requestId: request.id,
        approval,
      }).unwrap();
      const instrumentLabel = `${instrument.symbol} · ${instrument.exchange}`;
      recordDecision({
        key,
        kind: "request",
        request,
        decision: "approved",
        summary: `${request.symbol} approved as ${instrumentLabel}`,
        instrumentLabel,
        reason: null,
        warning: null,
        resolvedAt: new Date().toISOString(),
      });
      const isCountryChanged =
        sourceCountry !== undefined &&
        sourceCountry !== instrument.sourceCountry;
      if (isCountryChanged) {
        try {
          await setSourceCountry({
            instrumentId: instrument.id,
            sourceCountry,
          }).unwrap();
        } catch {
          setDecisionWarning(
            key,
            `${instrument.symbol} approved, but its source country wasn't saved`,
          );
        }
      }
    } catch (err: unknown) {
      failWith(request, err);
    }
  };

  const reject = async (request: PendingTickerRequest, reason: string) => {
    setFailure(null);
    try {
      await rejectRequest({ requestId: request.id, reason }).unwrap();
      recordDecision({
        key: requestKey(request.id),
        kind: "request",
        request,
        decision: "rejected",
        summary: `${request.symbol} rejected`,
        instrumentLabel: null,
        reason,
        warning: null,
        resolvedAt: new Date().toISOString(),
      });
    } catch (err: unknown) {
      failWith(request, err);
    }
  };

  const reads = [
    { category: "request" as const, read: requestsRead },
    { category: "instrument" as const, read: fixesRead },
    { category: "corporate" as const, read: actionsRead },
  ];
  const failedReads = reads.filter(({ read }) => read.isError);
  const unloadedReads = failedReads.filter(
    ({ read }) => read.data === undefined,
  );
  const isLoadFailed = unloadedReads.length === reads.length;
  const isLoading =
    requestsRead.isLoading || fixesRead.isLoading || actionsRead.isLoading;

  const retry = () => {
    failedReads.forEach(({ read }) => void read.refetch());
  };

  return {
    view,
    items,
    counts,
    selected,
    outcome,
    nextItem,
    isLoading,
    isLoadFailed,
    isRefreshFailed: failedReads.length > unloadedReads.length,
    unloadedCategories:
      isLoading || isLoadFailed
        ? []
        : unloadedReads.map(({ category }) => labelOf(category)),
    isSubmitting:
      isApproving || isRejecting || isSavingCountry || isFixing || isActing,
    // Includes the refetch, so a section that was just fixed can't be submitted again.
    isFixBlocked: isFixing || fixesRead.isFetching,
    isActionBlocked: isActing || actionsRead.isFetching,
    actionError: actionError ?? null,
    fixErrors,
    detachedError,
    select,
    changeCategory,
    changeFilters,
    changeOrder,
    clearError: () => setFailure(null),
    approve,
    reject,
    saveCountry,
    repair,
    applyManualPricing,
    deferAction,
    resumeAction,
    reconcileAction,
    retry,
  };
};
